import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DictionaryMembershipSelectControl } from '@/module/Nomen/DictionaryMembership/DictionaryMembershipSelectControl';
import { PublisherSelectControl } from '@/module/Nomen/Publisher/PublisherSelectControl';

import {
  AuthorListControl,
  Content,
  EnumSelectControl,
  Form,
  FormSubmit,
  NumberInputControl,
  TextInputControl,
} from '@/core/components';

import { DictionarySchema } from './model/DictionarySchema';

interface DictionaryFormProps extends FormSubmit<DictionarySchema> {
  title: string;
}

export function DictionaryForm(props: DictionaryFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<DictionarySchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <Group align="flex-end">
              <NumberInputControl<DictionarySchema>
                name="publicationYear"
                label={t('dictionaries.form.fields.publicationYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
              />
              <EnumSelectControl<DictionarySchema>
                name="type"
                label={t('dictionaries.form.fields.type')}
                isSubmitting={isSubmitting}
                options={[
                  { value: 'DICTIONARY', label: t('dictionaries.typeEnum.DICTIONARY') },
                  { value: 'ENCYCLOPEDIA', label: t('dictionaries.typeEnum.ENCYCLOPEDIA') },
                  { value: 'ATLAS', label: t('dictionaries.typeEnum.ATLAS') },
                ]}
              />
            </Group>
            <TextInputControl<DictionarySchema>
              name="title"
              label={t('dictionaries.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group align="flex-end">
              <PublisherSelectControl<DictionarySchema>
                name="publisher"
                label={t('dictionaries.form.fields.publisher')}
                isSubmitting={isSubmitting}
              />
              <EnumSelectControl<DictionarySchema>
                name="printType"
                label={t('dictionaries.form.fields.printType')}
                isSubmitting={isSubmitting}
                options={[
                  { value: 'PRINT', label: 'Print' },
                  { value: 'ONLINE', label: 'Online' },
                ]}
              />
              <NumberInputControl
                withAsterisk
                name="noOfPages"
                label={t('dictionaries.form.fields.noOfPages')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
              />
            </Group>
            <Group align="flex-end">
              <TextInputControl<DictionarySchema>
                name="isbnPrint"
                label={t('dictionaries.form.fields.isbnPrint')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<DictionarySchema>
                name="eisbn"
                label={t('dictionaries.form.fields.eisbn')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <TextInputControl<DictionarySchema>
              name="link"
              label={t('dictionaries.form.fields.link')}
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<DictionarySchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <DictionaryMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
